import {Component} from '@angular/core';
import {AsyncPipe, NgFor, NgIf} from '@angular/common';
import {combineLatest, map} from 'rxjs';
import {CallStatus, CallType} from '../call.service';
import {CallPreferencesService} from '../dnd-toggle/call-preferences.service';
import {DndToggleComponent} from '../dnd-toggle/dnd-toggle.component';
import {MissedCall, MissedCallService} from './missed-call.service';

/**
 * The missed-call banners (ITP-E-171-01): "Missed voice call from <Name>" or "Missed video call from <Name>"
 * (ITP-E-182-06) for a ring that ran out, "<Name> tried to call you" ("tried to video call you") for a call that came
 * while the user was in a call or a meeting — a banner, never an overlay, so it does
 * not interrupt. Each has Call back and a close button, and stays until one of them is used. Mounted beside the
 * call bar, outside the router outlet, so it survives navigation. The newest few show; the rest wait their turn.
 *
 * Under the banners sits the "Do not disturb for calls" switch (ITP-E-171-04). It also shows on its own while Do
 * not disturb is on, so the user always sees that calls are going straight to missed and can turn it off.
 */
@Component({
  selector: 'app-missed-call-banner',
  standalone: true,
  imports: [NgIf, NgFor, AsyncPipe, DndToggleComponent],
  templateUrl: './missed-call-banner.component.html',
  styleUrls: ['./missed-call-banner.component.scss']
})
export class MissedCallBannerComponent {
  static readonly maxVisible = 3;

  readonly view$ = combineLatest([this.missedCallService.missedCalls$, this.callPreferences.doNotDisturb$])
    .pipe(map(([calls, doNotDisturb]) => ({
      visible: calls.slice(0, MissedCallBannerComponent.maxVisible),
      hidden: Math.max(0, calls.length - MissedCallBannerComponent.maxVisible),
      doNotDisturb
    })));

  constructor(private missedCallService: MissedCallService,
              private callPreferences: CallPreferencesService) {
  }

  static message(call: MissedCall): string {
    const name = call.callerName || call.callerEmail || 'Someone';
    const video = MissedCallBannerComponent.isVideo(call);
    return call.status === CallStatus.Busy
      ? `${name} tried to ${video ? 'video ' : ''}call you`
      : `Missed ${video ? 'video' : 'voice'} call from ${name}`;
  }

  static isVideo(call: MissedCall): boolean {
    return call.type === CallType.Video;
  }

  isVideo(call: MissedCall): boolean {
    return MissedCallBannerComponent.isVideo(call);
  }

  messageOf(call: MissedCall): string {
    return MissedCallBannerComponent.message(call);
  }

  onCallBack(call: MissedCall): void {
    this.missedCallService.callBack(call).then();
  }

  onDismiss(call: MissedCall): void {
    this.missedCallService.dismiss(call.invitationId).then();
  }

  trackById(_: number, call: MissedCall): string {
    return call.invitationId;
  }
}
